import { SALES_AGREEMENT_DETAIL_BREADCRUMB } from '@features/TimberBuyer/SalesAgreements/constants'
import {
  getContractorQuery,
  getForestOwnerQuery,
  getPropertyDetailsQuery,
  getSalesAgreementQuery,
  getTimberBuyerQuery,
} from '@services/queryOptions'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useMatches, useParams } from '@tanstack/react-router'
import { formatCadastralNumber } from '@utils/cadastral-number-formatter'
import { formatFullName } from '@utils/helper-functions'
import { useTranslations } from '@utils/i18n-to-migrate/useTranslations'

interface DynamicCrumb {
  path: string
  name: string
  isDynamic?: boolean
}

export function useDynamicBreadcrumbs(): { breadcrumb_routes: DynamicCrumb[] } {
  const matches = useMatches()
  const location = useLocation()
  const params = useParams({ strict: false })
  const { t } = useTranslations()
  const fromSearch = location.state.fromSearch

  // Get forest owner data if we have an ID
  const forestOwnerId = params.forestownerId
  const { data: forestOwner } = useQuery({
    ...getForestOwnerQuery(forestOwnerId ?? ''),
    enabled: !!forestOwnerId,
  })

  const timberBuyerId = params.timberBuyerId
  const { data: timberBuyer } = useQuery({
    ...getTimberBuyerQuery(timberBuyerId ?? ''),
    enabled: !!timberBuyerId,
  })

  const contractorId = params.contractorId
  const { data: contractor } = useQuery({
    ...getContractorQuery(contractorId ?? ''),
    enabled: !!contractorId,
  })

  // Get property data if we have a property ID
  const propertyId = params.propertyId
  const { data: propertyDetails } = useQuery({
    ...getPropertyDetailsQuery(propertyId ?? ''),
    enabled: !!propertyId,
  })

  // Get sales agreement data if we have an ID
  const salesAgreementId = params.salesAgreementId
  const { data: salesAgreement } = useQuery({
    ...getSalesAgreementQuery(timberBuyerId ?? '', salesAgreementId ?? ''),
    enabled: !!timberBuyerId && !!salesAgreementId,
  })

  const breadcrumbs: DynamicCrumb[] = []
  const isForestCrewEditPage = /^\/contractor\/[^/]+\/forest-crews\/[^/]+\/edit\/?$/.test(
    location.pathname,
  )
  const isEmployeeEditPage = /^\/contractor\/[^/]+\/employees\/[^/]+\/edit\/?$/.test(
    location.pathname,
  )

  // Add search breadcrumb if coming from search (but not if we're currently on the search page)
  const isOnSearchPage = location.pathname === '/search'
  if (fromSearch && !isOnSearchPage) {
    let searchUrl = '/search'

    if (typeof fromSearch === 'string') {
      // New format: URL string like '/search?searchType=eiendom&municipality=...'
      searchUrl = fromSearch
    } else if (typeof fromSearch === 'object' && fromSearch.searchTerm) {
      // Legacy format: object with searchTerm for forest owner search
      searchUrl = `/search?searchType=skogeier&searchTerm=${encodeURIComponent(fromSearch.searchTerm)}`
    }

    breadcrumbs.push({
      path: searchUrl,
      name: 'search',
    })
  }

  // Check if we need to add forest owner name for sub-pages
  const hasForestOwnerRoute = matches.some(
    (match) =>
      match.staticData.breadcrumb === 'Skogeieroversikt' ||
      match.staticData.breadcrumb === 'Oversikt',
  )
  const needsForestOwnerName = forestOwnerId && forestOwner && hasForestOwnerRoute

  const hasTimberBuyerRoute = matches.some(
    (match) => match.staticData.breadcrumb === 'Tømmerkjøper',
  )
  const needsTimberBuyerName = timberBuyerId && timberBuyer && hasTimberBuyerRoute

  const hasContractorRoute = matches.some((match) => match.staticData.breadcrumb === 'Entreprenør')
  const needsContractorName = contractorId && contractor && hasContractorRoute

  const addPropertyRootBreadcrumb = () => {
    if (!forestOwnerId) {
      return
    }
    const propertyRootPath = `/forestowner/${forestOwnerId}/property`
    const hasPropertyRootBreadcrumb = breadcrumbs.some((crumb) => crumb.path === propertyRootPath)
    if (!hasPropertyRootBreadcrumb) {
      breadcrumbs.push({
        path: propertyRootPath,
        name: 'Eiendom',
        isDynamic: false,
      })
    }
  }

  const addContractorResourcesBreadcrumb = () => {
    if (!contractorId) {
      return
    }
    const contractorResourcesPath = `/contractor/${contractorId}/resources`
    const hasContractorResourcesBreadcrumb = breadcrumbs.some(
      (crumb) => crumb.path === contractorResourcesPath,
    )
    if (!hasContractorResourcesBreadcrumb) {
      breadcrumbs.push({
        path: contractorResourcesPath,
        name: t('contractor.profile.tabs.resources'),
        isDynamic: false,
      })
    }
  }

  const addTimberBuyerSalesAgreementsBreadcrumb = () => {
    if (!timberBuyerId) {
      return
    }
    const salesAgreementsPath = `/timberbuyer/${timberBuyerId}/sales-agreements`
    const hasSalesAgreementsBreadcrumb = breadcrumbs.some(
      (crumb) => crumb.path === salesAgreementsPath,
    )
    if (!hasSalesAgreementsBreadcrumb) {
      breadcrumbs.push({
        path: salesAgreementsPath,
        name: t('timberbuyer.profile.salesAgreements.title'),
        isDynamic: false,
      })
    }
  }

  // Process matches and apply dynamic transformations
  matches
    .filter((match) => match.pathname !== '/')
    .forEach((match) => {
      const staticBreadcrumb = match.staticData.breadcrumb
      const isPropertyAddRoute =
        match.pathname.endsWith('/property/add') ||
        match.pathname.endsWith('/property/supplier-number')
      const isForestCrewAddRoute = match.pathname.endsWith('/forest-crews/add')
      const isForestCrewStandaloneRoute = /^\/contractor\/[^/]+\/forest-crews\/.+/.test(
        match.pathname,
      )
      const isForestCrewDetailRoute = /^\/contractor\/[^/]+\/forest-crews\/[^/]+\/?$/.test(
        match.pathname,
      )
      const isEmployeeDetailRoute = /^\/contractor\/[^/]+\/employees\/[^/]+\/?$/.test(
        match.pathname,
      )
      const isSalesAgreementRegisterRoute = match.pathname.endsWith('/sales-agreements/register')
      const isSalesAgreementLayoutRoute =
        /\/sales-agreements\/[^/]+/.test(match.pathname) && !staticBreadcrumb
      const isSalesAgreementEditRoute = /\/sales-agreements\/[^/]+\/edit\/?$/.test(match.pathname)
      const isSalesAgreementDetailRoute = staticBreadcrumb === SALES_AGREEMENT_DETAIL_BREADCRUMB

      if (isPropertyAddRoute) {
        addPropertyRootBreadcrumb()
      }
      if (isForestCrewAddRoute || isForestCrewStandaloneRoute) {
        addContractorResourcesBreadcrumb()
      }
      if (isSalesAgreementRegisterRoute || isSalesAgreementEditRoute) {
        addTimberBuyerSalesAgreementsBreadcrumb()
      }
      if (isSalesAgreementDetailRoute) {
        addTimberBuyerSalesAgreementsBreadcrumb()
        breadcrumbs.push({
          path: match.pathname,
          name: salesAgreement?.name ?? staticBreadcrumb ?? match.pathname,
          isDynamic: true,
        })
        return
      }
      if (isSalesAgreementLayoutRoute) {
        // On the SA detail page the index match handles the SA name via isSalesAgreementDetailRoute.
        // On child pages (e.g. main-assortments) there is no Salgsavtale match in the chain,
        // so inject the SA name here as a clickable crumb pointing to the detail page.
        const isOnDetailIndex = matches.some(
          (m) => m.staticData.breadcrumb === SALES_AGREEMENT_DETAIL_BREADCRUMB,
        )
        if (!isOnDetailIndex && salesAgreement?.name) {
          addTimberBuyerSalesAgreementsBreadcrumb()
          breadcrumbs.push({
            path: `${match.pathname}/`,
            name: salesAgreement.name,
            isDynamic: true,
          })
        }
        return
      }
      if (isForestCrewEditPage && isForestCrewDetailRoute) {
        return
      }
      if (isEmployeeEditPage && isEmployeeDetailRoute) {
        return
      }

      // Skip "Skogeieroversikt" as per spec - forest owner name will be added separately for sub-pages
      if (staticBreadcrumb === 'Skogeieroversikt') {
        // Only add forest owner name if this is NOT the overview page (which has its own "Oversikt" breadcrumb)
        const hasOverviewRoute = matches.some((m) => m.staticData.breadcrumb === 'Oversikt')
        if (!hasOverviewRoute && needsForestOwnerName) {
          breadcrumbs.push({
            path: `/forestowner/${forestOwnerId}`,
            name: formatFullName(forestOwner.name),
            isDynamic: true,
          })
        }
        return
      }

      // Skip "Tømmerkjøper" and replace it with Timber Buyer name.
      // On overview pages, the name is rendered by transforming the "Oversikt" breadcrumb.
      // On sub-pages without an "Oversikt" match, add the name here as parent breadcrumb.
      if (staticBreadcrumb === 'Tømmerkjøper' && timberBuyerId) {
        const hasOverviewRoute = matches.some((m) => m.staticData.breadcrumb === 'Oversikt')
        if (!hasOverviewRoute && needsTimberBuyerName) {
          breadcrumbs.push({
            path: `/timberbuyer/${timberBuyerId}`,
            name: formatFullName(timberBuyer.name),
            isDynamic: true,
          })
        }
        return
      }

      // Skip "Entreprenør" and replace with contractor name.
      // On overview pages, the name is rendered by transforming "Oversikt".
      // On sub-pages, add the name here as parent breadcrumb.
      if (staticBreadcrumb === 'Entreprenør' && contractorId) {
        const hasOverviewRoute = matches.some((m) => m.staticData.breadcrumb === 'Oversikt')
        if (!hasOverviewRoute && needsContractorName) {
          breadcrumbs.push({
            path: `/contractor/${contractorId}`,
            name: formatFullName(contractor.name),
            isDynamic: true,
          })
        }
        return
      }

      let displayName: string = staticBreadcrumb ?? match.pathname
      let isDynamic = false

      // Transform "Oversikt" to forest owner name
      if (staticBreadcrumb === 'Oversikt' && forestOwner) {
        displayName = formatFullName(forestOwner.name)
        isDynamic = true
      } else if (staticBreadcrumb === 'Oversikt' && needsTimberBuyerName) {
        displayName = formatFullName(timberBuyer.name)
        isDynamic = true
      } else if (staticBreadcrumb === 'Oversikt' && needsContractorName) {
        displayName = formatFullName(contractor.name)
        isDynamic = true
      }

      // Transform "Tømmerkjøper" to timber buyer name
      if (staticBreadcrumb === 'Tømmerkjøper' && timberBuyerId && timberBuyer) {
        displayName = formatFullName(timberBuyer.name)
        isDynamic = true
      }

      // Handle "Eiendom" breadcrumb - add both "Eiendom" and property identifier
      if (staticBreadcrumb === 'Eiendom') {
        // First add "Eiendom" as a clickable breadcrumb that goes to the Eiendom tab
        addPropertyRootBreadcrumb()

        // Then add the property identifier if we have property details
        if (propertyDetails?.cadastralNumber) {
          // Format the cadastralNumber for display
          const formattedCadastralNumber = formatCadastralNumber(propertyDetails.cadastralNumber)
          breadcrumbs.push({
            path: match.pathname,
            name: formattedCadastralNumber,
            isDynamic: true,
          })
        }
        return // Skip the normal breadcrumb push below
      }

      // Checks if this is a sub-route of editShareOwnership
      const isEditShareOwnershipSubRoute =
        match.pathname.includes('/shareownership/editShareOwnership/') &&
        match.pathname !== `/forestowner/${forestOwnerId}/shareownership/editShareOwnership/`

      if (isEditShareOwnershipSubRoute) {
        // Ensure we have the parent "Endre andelsinformasjon" breadcrumb first
        const hasEditShareOwnershipBreadcrumb = breadcrumbs.some(
          (crumb) =>
            crumb.path === `/forestowner/${forestOwnerId}/shareownership/editShareOwnership/`,
        )

        if (!hasEditShareOwnershipBreadcrumb) {
          // Add the parent breadcrumb if it's missing
          breadcrumbs.push({
            path: `/forestowner/${forestOwnerId}/shareownership/editShareOwnership/`,
            name: 'Endre andelseierskap',
            isDynamic: false,
          })
        }
      }
      breadcrumbs.push({
        path: match.pathname,
        name: displayName,
        isDynamic,
      })
    })

  return { breadcrumb_routes: breadcrumbs }
}
